import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/useAuth'
import { supabase } from '../lib/supabaseClient'
import { Shield, Plus, Users, Target, LogOut, Settings, Building2, Menu, User } from 'lucide-react'
import GameEvent from '../components/GameEvent'
import CreateEvent from '../components/CreateEvent'
import ErrorBoundary from '../components/ErrorBoundary'
import ViewSwitcher from '../components/ViewSwitcher'
import NotificationBell from '../components/NotificationBell'
import WhatsNew from '../components/WhatsNew'
import FeatureGate from '../components/FeatureGate'
import { isPlatformStaff, isPlatformAdmin } from '../lib/roleHelpers'
import db from '../lib/db'
import cache from '../lib/localCache'
import { clearAllUserState } from '../lib/authCleanup'
import { eventStatusTag, eventStatusLabel } from '../lib/eventStatus'

// ── CIPHER STYLES ────────────────────────────────────────────
const C = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    color: '#00ff66',
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 4px rgba(0,255,102,.5)',
    position: 'relative',
    overflow: 'hidden',
  },
  // Scanline + vignette overlays applied via CSS class in App.css
  nav: {
    background: '#0a140a',
    borderBottom: '1px solid rgba(0,255,102,.18)',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 10,
  },
  sshBar: {
    background: 'rgba(0,255,102,.06)',
    borderBottom: '1px solid rgba(0,255,102,.12)',
    padding: '4px 16px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '10px',
    color: '#4aaa5a',
    letterSpacing: '0.08em',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 10,
  },
  logo: {
    fontFamily: "'VT323', 'Share Tech Mono', monospace",
    fontSize: '18px',
    color: '#00ff66',
    letterSpacing: '0.08em',
    textShadow: '0 0 6px rgba(0,255,102,.6)',
  },
  display: {
    fontFamily: "'VT323', 'Share Tech Mono', monospace",
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  mono: {
    fontFamily: "'JetBrains Mono', monospace",
  },
  phos: { color: '#00ff66' },
  dim: { color: '#4aaa5a' },
  deep: { color: '#2a6a3a' },
  glow: { color: '#9aff9a' },
  alert: { color: '#ff2a2a' },
  amber: { color: '#ffcc33' },
  panel: {
    background: 'rgba(0, 20, 8, .4)',
    border: '1px solid #00ff66',
    boxShadow: '0 0 12px rgba(0,255,102,.15), inset 0 0 20px rgba(0,255,102,.04)',
    position: 'relative',
  },
  panelDim: {
    background: 'rgba(0, 20, 8, .2)',
    border: '1px solid rgba(0,255,102,.18)',
    boxShadow: '0 0 6px rgba(0,255,102,.08)',
    position: 'relative',
  },
  btnPrimary: {
    fontFamily: "'VT323', monospace",
    fontSize: '14px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    background: '#00ff66',
    color: '#000000',
    border: 'none',
    padding: '8px 16px',
    cursor: 'pointer',
    boxShadow: '0 0 6px #00ff66',
    textShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  btnNav: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 20, 8, .4)',
    border: '1px solid rgba(0,255,102,.18)',
    color: '#4aaa5a',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tag: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '10px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '2px 8px',
    textShadow: 'none',
  },
  tagActive: {
    background: 'rgba(0,255,102,.08)',
    border: '1px solid rgba(0,255,102,.4)',
    color: '#00ff66',
  },
  tagPending: {
    background: 'rgba(255,204,51,.08)',
    border: '1px solid rgba(255,204,51,.3)',
    color: '#ffcc33',
  },
  tagClosed: {
    background: 'rgba(0,255,102,.04)',
    border: '1px solid rgba(0,255,102,.1)',
    color: '#2a6a3a',
  },
  cursor: {
    display: 'inline-block',
    width: '8px',
    height: '16px',
    background: '#00ff66',
    boxShadow: '0 0 6px #00ff66',
    verticalAlign: 'middle',
    marginLeft: '4px',
    animation: 'cipher-blink 1s steps(2) infinite',
  },
}

// Corner bracket component — reusable for any CIPHER panel
function Brackets({ children, style = {}, className = '', dim = false }) {
  const panelStyle = dim ? C.panelDim : C.panel
  return (
    <div className={className} style={{ ...panelStyle, ...style }}>
      {/* Top-left bracket */}
      <div style={{
        position: 'absolute', top: -1, left: -1, width: 8, height: 8,
        borderTop: `2px solid ${dim ? '#4aaa5a' : '#9aff9a'}`,
        borderLeft: `2px solid ${dim ? '#4aaa5a' : '#9aff9a'}`,
      }} />
      {/* Bottom-right bracket */}
      <div style={{
        position: 'absolute', bottom: -1, right: -1, width: 8, height: 8,
        borderBottom: `2px solid ${dim ? '#4aaa5a' : '#9aff9a'}`,
        borderRight: `2px solid ${dim ? '#4aaa5a' : '#9aff9a'}`,
      }} />
      {children}
    </div>
  )
}

export default function Dashboard() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [showCreateEvent, setShowCreateEvent] = useState(false)
  const [activeEvent, setActiveSession] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isOrgAdmin, setIsOrgAdmin] = useState(false)
  // Bug 44 — event creation requires platform staff OR an org_owner/
  // admin/gm membership. Plain event-role co_gm is NOT enough — that
  // role is event-scoped, not org-scoped. Tracked separately from
  // isOrgAdmin (which excludes org_gm).
  const [canCreateEvent, setCanCreateEvent] = useState(false)
  // Bug 60 — archived sections default collapsed so the active list is
  // first thing on screen. User's explicit choice (either way) persists.
  const [closedCollapsed, setClosedCollapsed] = useState(() =>
    localStorage.getItem('dc_dashboard_closed_collapsed') !== 'false'
  )
  const [navMenuOpen, setNavMenuOpen] = useState(false)

  const now = new Date()
  const uptime = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`

  // ANU-37 — auto-select the live event when the GM lands on the
  // command center. Single live event → drop straight into it. Multiple
  // → pick the most recently live_at. Fires once per Dashboard mount so
  // hitting BACK from inside the event still surfaces the dashboard.
  const didAutoSelectRef = useRef(false)

  useEffect(() => {
    // Clear events cache on mount to ensure fresh data
    cache.clear('events').catch(() => {})
    fetchUserProfile()
    fetchEvents()
  }, [])

  useEffect(() => {
    if (didAutoSelectRef.current) return
    if (loading || events.length === 0) return
    const liveEvents = events.filter(e => e.status === 'live')
    if (liveEvents.length === 0) {
      // No live events on this load — don't claim the auto-select slot,
      // so if one goes live later (e.g. via realtime cache flush) we
      // can still snap into it.
      return
    }
    liveEvents.sort((a, b) => {
      const ta = new Date(a.live_at || a.created_at || 0).getTime()
      const tb = new Date(b.live_at || b.created_at || 0).getTime()
      return tb - ta
    })
    didAutoSelectRef.current = true
    setActiveSession(liveEvents[0])
  }, [events, loading])

  useEffect(() => {
    // Re-evaluate org-admin gate once profile is loaded — m55_admin
    // shortcut needs profile.role, which isn't available on mount.
    checkOrgAdmin()
  }, [profile?.role])

  const fetchUserProfile = async () => {
    const data = await db.getUserProfile(user.id)
    setUserProfile(data || null)
  }

  const fetchEvents = async () => {
    // Bug 32: org-scope the events list unless the viewer is platform staff
    // (m55_admin / m55_staff / m55_support). org_gm members should only see
    // events for their org(s); platform staff see everything.
    const data = isPlatformStaff(profile)
      ? await db.getAllEvents()
      : await db.getMyOrgEvents(user.id)
    setEvents(data || [])
    setLoading(false)
  }

  const checkOrgAdmin = async () => {
    if (isPlatformStaff(profile)) {
      setIsOrgAdmin(true)
      setCanCreateEvent(true)
      return
    }
    const [isAdmin, canCreate, coGmCheck] = await Promise.all([
      db.getIsOrgAdmin(user.id),
      db.getCanCreateEvent(user.id),
      supabase.from('event_players').select('user_id').eq('user_id', user.id).eq('event_role', 'co_gm').limit(1),
    ])
    // co_gm is scoped per-event in event_players, not in org_members.
    // Grant admin-view access so their CC button appears on the dashboard.
    setIsOrgAdmin(isAdmin || (coGmCheck.data?.length > 0))
    setCanCreateEvent(canCreate)
  }

  const handleSignOut = async () => {
    // Track 1.1 — flush user state BEFORE auth.signOut() so the cache /
    // realtime channels / localStorage are gone by the time the redirect
    // tears the tab down. The SIGNED_OUT auth event also calls this, but
    // doing it here too is the spec'd belt-and-suspenders.
    await clearAllUserState()
    await supabase.auth.signOut()
    window.location.href = '/auth'
  }

  if (activeEvent) {
    return <GameEvent event={activeEvent} user={userProfile} onBack={() => setActiveSession(null)} />
  }

  // Cancelled events drop into the archive alongside closed ones (Bug 57).
  // Without this they lingered in the active list with no path back.
  const activeEvents = events.filter(s => s.status !== 'closed' && s.status !== 'cancelled')
  const closedEvents = events.filter(s => s.status === 'closed' || s.status === 'cancelled')

  return (
    <div style={C.page}>
      {/* Blink animation for cursor */}
      <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>

      <WhatsNew />

      {/* ── SSH BAR ── */}
      <div style={C.sshBar}>
        <span>root@anubis:~/ops #{' '}</span>
        <span>
          ENC OK · {now.toISOString().slice(0,10)} · UPTIME {uptime}
        </span>
      </div>

      {/* ── NAV ── */}
      <nav style={C.nav}>
        <div className="flex items-center gap-2">
          <img src="/anubis-logo.png" alt="Anubis" style={{ height: 32, width: 'auto', display: 'block' }} />
        </div>
        <div className="flex items-center gap-2 relative">
          <NotificationBell tone="cipher" />
          <span className="hidden sm:inline-flex"><ViewSwitcher currentView="gm" compact /></span>
          <button
            onClick={() => window.location.href = '/profile'}
            className="hidden sm:flex w-8 h-8 items-center justify-center font-bold text-sm"
            style={{ background: '#00ff66', color: '#000', textShadow: 'none' }}
            title={profile?.callsign || profile?.display_name || user?.email}
          >
            {(profile?.display_name || user?.email || '?')[0].toUpperCase()}
          </button>
          {/* Desktop: secondary nav items */}
          <div className="hidden sm:flex items-center gap-2">
            {isOrgAdmin && (
              <a href="/org" style={C.btnNav} title="Org Admin"><Building2 size={16} /></a>
            )}
            {isPlatformStaff(profile) && (
              <a href="/teams" style={C.btnNav} title="Teams"><Users size={16} /></a>
            )}
            {isPlatformAdmin(profile) && (
              <a href="/admin" style={C.btnNav} title="Admin panel"><Settings size={16} /></a>
            )}
            <button onClick={handleSignOut} style={{ ...C.btnNav, color: '#2a6a3a' }} title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
          {/* Mobile: hamburger for secondary items */}
          <button
            onClick={() => setNavMenuOpen(o => !o)}
            className="sm:hidden flex items-center justify-center w-8 h-8"
            style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)' }}
            aria-label="More navigation"
          >
            <Menu size={16} />
          </button>
        </div>
      </nav>

      {/* ANU-124: drawer outside <nav> — nav has position:relative+zIndex which creates a stacking
          context; fixed children inside it are confined to that context and lose to <main> at
          the same z-index level. Sibling placement escapes the context. */}
      {navMenuOpen && (
        <>
          <div
            onClick={() => setNavMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.3)' }}
          />
          <div
            className="sm:hidden"
            style={{
              position: 'fixed', top: 52, right: 12, zIndex: 10001,
              background: '#0a140a', border: '1px solid rgba(0,255,102,.2)',
              padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4,
              boxShadow: '0 8px 20px rgba(0,0,0,.6)',
            }}
          >
            <ViewSwitcher currentView="gm" />
            <a
              href="/profile"
              style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px', color: '#4aaa5a', textDecoration: 'none' }}
            >
              <User size={16} /><span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>PROFILE</span>
            </a>
            {isOrgAdmin && (
              <a href="/org" style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px', color: '#4aaa5a', textDecoration: 'none' }}>
                <Building2 size={16} /><span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>ORG ADMIN</span>
              </a>
            )}
            {isPlatformStaff(profile) && (
              <a href="/teams" style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px', color: '#4aaa5a', textDecoration: 'none' }}>
                <Users size={16} /><span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>TEAMS</span>
              </a>
            )}
            {isPlatformAdmin(profile) && (
              <a href="/admin" style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px', color: '#4aaa5a', textDecoration: 'none' }}>
                <Settings size={16} /><span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>ADMIN</span>
              </a>
            )}
            <button
              onClick={() => { setNavMenuOpen(false); handleSignOut() }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 12px', background: 'none', border: 'none', cursor: 'pointer', color: '#2a6a3a', width: '100%', textAlign: 'left' }}
            >
              <LogOut size={16} /><span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>SIGN OUT</span>
            </button>
          </div>
        </>
      )}

      {/* ── MAIN ── */}
      <main className="max-w-4xl mx-auto p-4" style={{ position: 'relative', zIndex: 10 }}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold" style={C.display}>
              Operations Center <span style={C.cursor} />
            </h2>
            <p className="text-sm mt-0.5" style={C.dim}>
              {activeEvents.length} active · {closedEvents.length} archived · {events.length} total ops
            </p>
          </div>

          {/* Bug 44 — only org_gm/org_admin/org_owner/platform staff can
              create events. Plain event-scoped co_gm should NOT see this
              button. FeatureGate (org-tier check) still wraps so the
              upgrade prompt surfaces for unfunded orgs. */}
          {canCreateEvent && (
            <FeatureGate feature="create_event" fallback={
              <span className="text-xs px-3 py-1" style={{
                ...C.tag, border: '1px solid rgba(0,255,102,.18)', color: '#2a6a3a',
              }}>
                UPGRADE TO CREATE EVENTS
              </span>
            }>
              <button onClick={() => setShowCreateEvent(true)} style={C.btnPrimary}>
                <Plus size={16} />
                <span className="hidden sm:inline">NEW OP</span>
              </button>
            </FeatureGate>
          )}
        </div>

        {/* ── PROCESS TABLE HEADER ── */}
        <div className="mb-2 px-1 flex justify-between" style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          <span>PID{'  '}OP{'                        '}STATUS</span>
          <span>ORG</span>
        </div>
        <div style={{ borderBottom: '1px solid rgba(0,255,102,.18)', marginBottom: '8px' }} />

        {loading ? (
          <p style={C.dim}>
            Loading operations<span style={C.cursor} />
          </p>
        ) : events.length === 0 ? (
          <div className="text-center py-16" style={C.dim}>
            <pre style={{ fontSize: '24px', lineHeight: 1.2, marginBottom: '16px', color: '#2a6a3a' }}>
{`  _____
 /     \\
| () () |
|  ___  |
|_____|_|`}
            </pre>
            <p style={{ ...C.display, fontSize: '16px', color: '#4aaa5a' }}>NO OPERATIONS FOUND</p>
            <p style={{ fontSize: '12px', color: '#2a6a3a', marginTop: '4px' }}>
              ./create --new-event to initialize
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Active events */}
            {activeEvents.map((session, i) => (
              <Brackets
                key={session.id}
                className="p-3 cursor-pointer transition-all"
                style={{ marginBottom: '8px' }}
              >
                <div
                  onClick={() => setActiveSession(session)}
                  className="flex justify-between items-start gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <span style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a' }}>
                        {String(i + 1).padStart(4, '0')}
                      </span>
                      <h3 className="font-bold text-base truncate" style={{
                        ...C.display, fontSize: '16px',
                      }}>
                        {session.name}
                      </h3>
                    </div>
                    <p className="text-sm mt-0.5 ml-12" style={C.dim}>
                      {session.organizations?.name || 'NO ORG ASSIGNED'}
                    </p>
                  </div>
                  <span style={{
                    ...C.tag,
                    ...(eventStatusTag[session.status] || eventStatusTag.draft),
                  }}>
                    {eventStatusLabel(session.status)}
                  </span>
                </div>
              </Brackets>
            ))}

            {/* Closed events */}
            {closedEvents.length > 0 && (
              <div>
                <button
                  onClick={() => {
                    const next = !closedCollapsed
                    setClosedCollapsed(next)
                    localStorage.setItem('dc_dashboard_closed_collapsed', String(next))
                  }}
                  className="w-full flex items-center justify-between px-1 py-2"
                >
                  <span style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    ARCHIVED — {closedEvents.length} OPS
                  </span>
                  <span style={{ fontSize: '10px', color: '#2a6a3a' }}>
                    {closedCollapsed ? '▼ show' : '▲ hide'}
                  </span>
                </button>
                {!closedCollapsed && (
                  <div className="space-y-2">
                    {closedEvents.map((session, i) => (
                      <Brackets
                        key={session.id}
                        dim
                        className="p-3 cursor-pointer transition-all"
                        style={{ opacity: 0.5, marginBottom: '6px' }}
                      >
                        <div
                          onClick={() => setActiveSession(session)}
                          className="flex justify-between items-start gap-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                              <span style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a' }}>
                                {String(activeEvents.length + i + 1).padStart(4, '0')}
                              </span>
                              <h3 className="font-bold text-base truncate" style={{
                                ...C.display, fontSize: '16px', color: '#4aaa5a',
                              }}>
                                {session.name}
                              </h3>
                            </div>
                            <p className="text-sm mt-0.5 ml-12" style={C.deep}>
                              {session.organizations?.name || 'NO ORG ASSIGNED'}
                            </p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <span style={{ ...C.tag, ...(eventStatusTag[session.status] || eventStatusTag.closed) }}>
                              {eventStatusLabel(session.status)}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/event/${session.id}/aar`) }}
                              style={{
                                ...C.tag,
                                background: 'rgba(255,204,51,.06)',
                                border: '1px solid rgba(255,204,51,.2)',
                                color: '#8a7a3a',
                                cursor: 'pointer',
                                textShadow: 'none',
                              }}
                            >
                              AAR →
                            </button>
                          </div>
                        </div>
                      </Brackets>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── TERMINAL FOOTER ── */}
        <div className="mt-8 pt-4" style={{ borderTop: '1px solid rgba(0,255,102,.1)' }}>
          <p style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a' }}>
            root@anubis:~/ops $ <span style={{ color: '#4aaa5a' }}>status --all</span>
          </p>
          <p style={{ ...C.mono, fontSize: '10px', color: '#2a6a3a', marginTop: '2px' }}>
            {activeEvents.length} running · {closedEvents.length} stopped · uptime {uptime}
          </p>
        </div>
      </main>

      {showCreateEvent && (
        <ErrorBoundary onError={() => { /* keep modal closed on crash; user can retry */ }}>
          <CreateEvent
            user={user}
            profile={profile}
            onClose={() => setShowCreateEvent(false)}
            onCreated={async (session) => {
              setShowCreateEvent(false)
              // Bust SWR caches so the new event + auto-seeded GM rows
              // surface immediately. Without this, fetchEvents returns the
              // pre-create snapshot and a stale event_players read makes
              // the creator look absent from their own roster (Bug 34).
              await cache.clear('events').catch(() => {})
              await cache.clear('event_players').catch(() => {})
              await fetchEvents()
            }}
          />
        </ErrorBoundary>
      )}
    </div>
  )
}
